import { useState } from 'react'
import { ExternalLink, Loader2, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  type BoardPostDraft,
  type BoardPostDraftSuggestedPostType,
  type BoardPostDraftStatus,
  boardDraftDetectedTypeLabel,
  boardDraftPostTypeLabel,
  formatBoardDraftSourcePostDate,
  resolveDraftDetectedType,
  discardBoardPostDraft,
  publishBoardPostDraft,
  updateBoardPostDraft,
} from '@/lib/boardPostDraftsApi'
import type { CommunityAudience } from '@/lib/communityPostData'

interface BoardPostDraftCardProps {
  readonly draft: BoardPostDraft
  readonly onDraftUpdated: (updatedDraft: BoardPostDraft) => void
  readonly onDraftRemoved: (draftId: string) => void
}

export function BoardPostDraftCard({
  draft,
  onDraftUpdated,
  onDraftRemoved,
}: BoardPostDraftCardProps) {
  const [suggestedTitle, setSuggestedTitle] = useState(draft.suggestedTitle)
  const [suggestedBody, setSuggestedBody] = useState(draft.suggestedBody ?? '')
  const [imageUrls, setImageUrls] = useState<string[]>([...draft.imageUrls])
  const [suggestedPostType, setSuggestedPostType] = useState<BoardPostDraftSuggestedPostType>(
    draft.suggestedPostType,
  )
  const [artistCity, setArtistCity] = useState(draft.artistCity ?? '')
  const [artistCountry, setArtistCountry] = useState(draft.artistCountry ?? '')
  const [publishAudience, setPublishAudience] = useState<CommunityAudience>('everyone')
  const [primaryPortfolioImageIndex, setPrimaryPortfolioImageIndex] = useState(
    draft.primaryPortfolioImageIndex ?? 0,
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [isDiscarding, setIsDiscarding] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const isReadOnly = draft.status !== 'pending'
  const showPrimaryPortfolioImagePicker =
    suggestedPostType === 'new_tattoo' && imageUrls.length > 0

  const handleRemoveImage = (imageIndex: number) => {
    setImageUrls((previousUrls) => {
      const nextUrls = previousUrls.filter((_, index) => index !== imageIndex)
      setPrimaryPortfolioImageIndex((previousPrimaryIndex) => {
        if (nextUrls.length === 0) return 0
        if (imageIndex === previousPrimaryIndex) return 0
        if (imageIndex < previousPrimaryIndex) return Math.max(0, previousPrimaryIndex - 1)
        return Math.min(previousPrimaryIndex, nextUrls.length - 1)
      })
      return nextUrls
    })
  }

  const handleSaveEdits = async () => {
    setIsSaving(true)
    setActionError(null)
    try {
      const updatedDraft = await updateBoardPostDraft(draft.id, {
        suggestedTitle,
        suggestedBody: suggestedBody.trim() ? suggestedBody : null,
        imageUrls,
        suggestedPostType,
        primaryPortfolioImageIndex:
          suggestedPostType === 'new_tattoo' ? primaryPortfolioImageIndex : undefined,
        artistCity: artistCity.trim() || null,
        artistCountry: artistCountry.trim() || null,
      })
      onDraftUpdated(updatedDraft)
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Failed to save draft')
    } finally {
      setIsSaving(false)
    }
  }

  const handlePublish = async () => {
    setIsPublishing(true)
    setActionError(null)
    try {
      await updateBoardPostDraft(draft.id, {
        suggestedTitle,
        suggestedBody: suggestedBody.trim() ? suggestedBody : null,
        imageUrls,
        suggestedPostType,
        primaryPortfolioImageIndex:
          suggestedPostType === 'new_tattoo' ? primaryPortfolioImageIndex : undefined,
        artistCity: artistCity.trim() || null,
        artistCountry: artistCountry.trim() || null,
      })
      const { draft: publishedDraft } = await publishBoardPostDraft(draft.id, {
        city: artistCity.trim() || undefined,
        country: artistCountry.trim() || null,
        audience: publishAudience,
        authorDisplayName: draft.sourceInstagramHandle
          ? `@${draft.sourceInstagramHandle}`
          : null,
        primaryPortfolioImageIndex:
          suggestedPostType === 'new_tattoo' ? primaryPortfolioImageIndex : undefined,
      })
      onDraftUpdated(publishedDraft)
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Failed to publish draft')
    } finally {
      setIsPublishing(false)
    }
  }

  const handleDiscard = async () => {
    setIsDiscarding(true)
    setActionError(null)
    try {
      const discardedDraft = await discardBoardPostDraft(draft.id)
      onDraftUpdated(discardedDraft)
      onDraftRemoved(draft.id)
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Failed to discard draft')
    } finally {
      setIsDiscarding(false)
    }
  }

  const statusBadgeVariant = (status: BoardPostDraftStatus): 'default' | 'secondary' | 'outline' => {
    if (status === 'pending') return 'default'
    if (status === 'published') return 'secondary'
    return 'outline'
  }

  return (
    <article className="rounded-xl border border-border bg-card p-4 md:p-6 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold">@{draft.sourceInstagramHandle}</h2>
            <Badge variant={statusBadgeVariant(draft.status)}>{draft.status}</Badge>
            <Badge variant="outline">
              {boardDraftDetectedTypeLabel(resolveDraftDetectedType(draft))}
            </Badge>
            <Badge variant="outline">{boardDraftPostTypeLabel(suggestedPostType)}</Badge>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            {draft.sourcePostUrl ? (
              <a
                href={draft.sourcePostUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                View source post
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            ) : null}
            {formatBoardDraftSourcePostDate(draft.sourcePostTimestamp) ? (
              <span className="text-muted-foreground">
                Posted {formatBoardDraftSourcePostDate(draft.sourcePostTimestamp)}
              </span>
            ) : (
              <span className="text-muted-foreground">Post date unknown</span>
            )}
          </div>
          {draft.classificationReason ? (
            <p className="text-sm text-muted-foreground">{draft.classificationReason}</p>
          ) : null}
        </div>
        {draft.publishedPostId ? (
          <p className="text-xs text-muted-foreground">Published post: {draft.publishedPostId}</p>
        ) : null}
      </div>

      {showPrimaryPortfolioImagePicker ? (
        <p className="text-sm text-muted-foreground">
          Tap an image to set the main tattoo photo (used for the linked portfolio item on publish).
        </p>
      ) : null}

      {imageUrls.length > 0 ? (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {imageUrls.map((imageUrl, imageIndex) => {
            const isPrimaryPortfolioImage =
              showPrimaryPortfolioImagePicker && imageIndex === primaryPortfolioImageIndex
            return (
              <div key={`${imageUrl}-${imageIndex}`} className="relative shrink-0">
                <button
                  type="button"
                  disabled={isReadOnly || !showPrimaryPortfolioImagePicker}
                  onClick={() => setPrimaryPortfolioImageIndex(imageIndex)}
                  className={`block rounded-lg ${showPrimaryPortfolioImagePicker && !isReadOnly ? 'cursor-pointer' : ''}`}
                >
                  <img
                    src={imageUrl}
                    alt={`Draft slide ${imageIndex + 1}`}
                    className={`h-28 w-28 rounded-lg object-cover md:h-36 md:w-36 ${
                      isPrimaryPortfolioImage
                        ? 'ring-2 ring-primary ring-offset-2 ring-offset-background'
                        : ''
                    }`}
                  />
                </button>
                {isPrimaryPortfolioImage ? (
                  <span className="absolute bottom-1 left-1 rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground">
                    Main tattoo
                  </span>
                ) : null}
                {!isReadOnly ? (
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(imageIndex)}
                    className="absolute right-1 top-1 rounded-full bg-background/90 p-1 shadow"
                    aria-label="Remove image"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            )
          })}
        </div>
      ) : (
        <p className="text-sm text-destructive">No images — add source images before publishing.</p>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`title-${draft.id}`}>Title</Label>
          <Input
            id={`title-${draft.id}`}
            value={suggestedTitle}
            onChange={(event) => setSuggestedTitle(event.target.value)}
            disabled={isReadOnly}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`post-type-${draft.id}`}>Post type</Label>
          <Select
            value={suggestedPostType}
            onValueChange={(value) =>
              setSuggestedPostType(value as BoardPostDraftSuggestedPostType)
            }
            disabled={isReadOnly}
          >
            <SelectTrigger id={`post-type-${draft.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new_tattoo">New tattoo</SelectItem>
              <SelectItem value="general">General</SelectItem>
              <SelectItem value="flash_drop">Flash drop</SelectItem>
              <SelectItem value="deal">Deal</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor={`body-${draft.id}`}>Body</Label>
          <Textarea
            id={`body-${draft.id}`}
            value={suggestedBody}
            onChange={(event) => setSuggestedBody(event.target.value)}
            rows={5}
            disabled={isReadOnly}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`city-${draft.id}`}>City</Label>
          <Input
            id={`city-${draft.id}`}
            value={artistCity}
            onChange={(event) => setArtistCity(event.target.value)}
            disabled={isReadOnly}
            placeholder="Helsinki"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`country-${draft.id}`}>Country</Label>
          <Input
            id={`country-${draft.id}`}
            value={artistCountry}
            onChange={(event) => setArtistCountry(event.target.value)}
            disabled={isReadOnly}
            placeholder="Finland"
          />
        </div>
        {!isReadOnly ? (
          <div className="space-y-2">
            <Label htmlFor={`audience-${draft.id}`}>Audience</Label>
            <Select
              value={publishAudience}
              onValueChange={(value) => setPublishAudience(value as CommunityAudience)}
            >
              <SelectTrigger id={`audience-${draft.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="everyone">Everyone</SelectItem>
                <SelectItem value="artists">Artists only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>

      {draft.sourcePostCaption ? (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">Original caption</summary>
          <p className="mt-2 whitespace-pre-wrap text-foreground/80">{draft.sourcePostCaption}</p>
        </details>
      ) : null}

      {actionError ? <p className="text-sm text-destructive">{actionError}</p> : null}

      {!isReadOnly ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Button variant="outline" onClick={handleSaveEdits} disabled={isSaving || isPublishing}>
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save edits
          </Button>
          <Button onClick={handlePublish} disabled={isPublishing || isSaving || imageUrls.length === 0}>
            {isPublishing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Publish to board
          </Button>
          <Button
            variant="destructive"
            onClick={handleDiscard}
            disabled={isDiscarding || isPublishing || isSaving}
          >
            {isDiscarding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
            Discard
          </Button>
        </div>
      ) : null}
    </article>
  )
}
